import { z } from "zod";
import {
	upsertSiteClone,
	upsertSiteDesign,
	type ChatCompanyResearch,
	type ChatConnect,
	type ChatSiteClone,
	type ChatSiteDesign,
} from "./messages.js";

const chatTurnSchema = z.object({
	role: z.enum(["user", "assistant"]),
	content: z.string(),
});

const chatErrSchema = z.object({
	error: z.string(),
	issues: z.array(z.string()).optional(),
});

const handoffSnapshotSchema = z.object({
	handoffId: z.string(),
	agentType: z.string(),
	agentLabel: z.string(),
	task: z.string(),
	phase: z.enum(["started", "progress", "done"]),
	/** Live activity line while phase is progress (“Updating src/…”). */
	note: z.string().optional(),
	/** Latest screenshot the agent took while verifying its work. */
	screenshotUrl: z.string().optional(),
	status: z
		.enum(["completed", "pending", "unavailable", "failed"])
		.optional(),
	summary: z.string().optional(),
	approvalId: z.string().optional(),
	openAgentId: z.string().optional(),
	previewUrl: z.string().url().optional(),
});

export type ChatHandoff = z.infer<typeof handoffSnapshotSchema>;

const connectSnapshotSchema = z.object({
	connectId: z.string(),
	provider: z.string(),
	label: z.string(),
	phase: z.enum(["awaiting", "connected", "timed_out"]),
	reason: z.string().optional(),
	authorizeUrl: z.string().optional(),
	portfolioCount: z.number().optional(),
});

const streamEventSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("reasoning"), text: z.string() }),
	z.object({ type: z.literal("text"), text: z.string() }),
	z.object({
		type: z.literal("handoff"),
		phase: z.enum(["started", "progress", "done"]),
		handoffId: z.string(),
		agentType: z.string(),
		agentLabel: z.string(),
		task: z.string(),
		intent: z.string().optional(),
		note: z.string().optional(),
		screenshotUrl: z.string().optional(),
		status: z
			.enum(["completed", "pending", "unavailable", "failed"])
			.optional(),
		summary: z.string().optional(),
		approvalId: z.string().optional(),
		openAgentId: z.string().optional(),
		previewUrl: z.string().url().optional(),
	}),
	z.object({
		type: z.literal("connect"),
		phase: z.enum(["awaiting", "connected", "timed_out"]),
		connectId: z.string(),
		provider: z.string(),
		label: z.string(),
		reason: z.string().optional(),
		authorizeUrl: z.string().optional(),
		portfolioCount: z.number().optional(),
	}),
	z.object({
		type: z.literal("site_design"),
		phase: z.enum([
			"started",
			"inventorying",
			"briefing",
			"designing",
			"media",
			"framing",
			"awaiting_design",
			"generating",
			"ready",
			"failed",
			"progress",
		]),
		siteId: z.string(),
		name: z.string(),
		sourceUrl: z.string(),
		progress: z.number(),
		status: z.string(),
		previewUrl: z.string().url().nullable().optional(),
		lastError: z.string().nullable().optional(),
		keep: z.array(z.object({ label: z.string(), reason: z.string() })).optional(),
		add: z.array(z.object({ label: z.string(), reason: z.string() })).optional(),
		drop: z.array(z.object({ label: z.string(), reason: z.string() })).optional(),
		stills: z
			.array(z.object({ id: z.string(), title: z.string(), page: z.string() }))
			.optional(),
		lastFile: z.string().nullable().optional(),
		filesWritten: z.array(z.string()).optional(),
	}),
	z.object({
		type: z.literal("site_publish"),
		phase: z.enum(["started", "pending_approval", "deployed", "failed", "progress"]),
		siteId: z.string(),
		approvalId: z.string().optional(),
		deployStatus: z.string().optional(),
		siteStatus: z.string().optional(),
		hostname: z.string().optional(),
		commitSha: z.string().optional(),
		repoUrl: z.string().optional(),
		publishedUrl: z.string().optional(),
		takesPayments: z.boolean().optional(),
	}),
	z.object({
		type: z.literal("company_research"),
		phase: z.enum(["started", "progress", "ready", "failed"]),
		packId: z.string(),
		siteUrl: z.string(),
		progress: z.number(),
		status: z.string(),
		brandName: z.string().optional(),
		lastError: z.string().nullable().optional(),
		card: z
			.object({
				brandName: z.string(),
				oneLiner: z.string(),
				siteUrl: z.string(),
				offers: z.array(z.string()),
				audience: z.string().nullable(),
				tone: z.string(),
				notablePages: z.array(
					z.object({ label: z.string(), url: z.string() }),
				),
				profiles: z.array(
					z.object({ network: z.string(), url: z.string() }),
				),
				openQuestions: z.array(z.string()),
			})
			.nullable()
			.optional(),
	}),
	z.object({
		type: z.literal("done"),
		modelId: z.string().optional(),
		text: z.string().optional(),
		reasoning: z.string().optional(),
	}),
	z.object({ type: z.literal("error"), error: z.string() }),
]);

export type AgentChatTurn = z.infer<typeof chatTurnSchema>;

export type StreamAgentChatDelta = {
	reasoning: string;
	text: string;
	handoffs: ChatHandoff[];
	connects: ChatConnect[];
	siteClones: ChatSiteClone[];
	siteDesigns: ChatSiteDesign[];
	companyResearch: ChatCompanyResearch[];
};

export type StreamAgentChatResult =
	| {
			ok: true;
			text: string;
			reasoning?: string;
			modelId?: string;
			handoffs: ChatHandoff[];
			connects: ChatConnect[];
			siteClones: ChatSiteClone[];
			siteDesigns: ChatSiteDesign[];
			companyResearch: ChatCompanyResearch[];
	  }
	| { ok: false; error: string };

/**
 * Streams `/api/llm/chat` NDJSON events (reasoning + text + handoffs) into `onDelta`.
 */
export type AgentChatAttachment = {
	mediaType: string;
	url: string;
	filename?: string;
};

function upsertHandoff(
	list: ChatHandoff[],
	next: ChatHandoff,
): ChatHandoff[] {
	const idx = list.findIndex((row) => row.handoffId === next.handoffId);
	if (idx < 0) return [...list, next];
	const copy = [...list];
	// Drop undefined keys so a later event (e.g. "done" without screenshotUrl)
	// never clobbers a field an earlier progress event set.
	const patch = Object.fromEntries(
		Object.entries(next).filter(([, value]) => value !== undefined),
	) as ChatHandoff;
	copy[idx] = { ...copy[idx], ...patch };
	return copy;
}

function upsertConnect(
	list: ChatConnect[],
	next: ChatConnect,
): ChatConnect[] {
	const idx = list.findIndex((row) => row.connectId === next.connectId);
	if (idx < 0) return [...list, next];
	const copy = [...list];
	copy[idx] = { ...copy[idx], ...next };
	return copy;
}

function upsertCompanyResearch(
	list: ChatCompanyResearch[],
	next: ChatCompanyResearch,
): ChatCompanyResearch[] {
	const idx = list.findIndex((row) => row.packId === next.packId);
	if (idx < 0) return [...list, next];
	const copy = [...list];
	copy[idx] = { ...copy[idx], ...next };
	return copy;
}

export async function streamAgentChat(args: {
	agentId: string;
	/** DeskChatAgent thread key — persists transcript for reconnect. */
	threadId?: string;
	messages: AgentChatTurn[];
	modelId?: string;
	attachments?: AgentChatAttachment[];
	onDelta: (delta: StreamAgentChatDelta) => void;
	signal?: AbortSignal;
}): Promise<StreamAgentChatResult> {
	try {
		const response = await fetch("/api/llm/chat", {
			method: "POST",
			credentials: "include",
			headers: {
				"content-type": "application/json",
				accept: "application/x-ndjson, text/plain",
			},
			body: JSON.stringify({
				agentId: args.agentId,
				...(args.threadId ? { threadId: args.threadId } : {}),
				messages: args.messages,
				...(args.modelId ? { modelId: args.modelId } : {}),
				...(args.attachments && args.attachments.length > 0
					? { attachments: args.attachments }
					: {}),
			}),
			signal: args.signal,
		});

		const contentType = response.headers.get("content-type") ?? "";
		const modelIdHeader =
			response.headers.get("x-mairko-model-id") ?? undefined;

		if (!response.ok) {
			const json: unknown = await response.json().catch(() => null);
			const err = chatErrSchema.safeParse(json);
			return {
				ok: false,
				error: err.success ? err.data.error : `http_${response.status}`,
			};
		}

		if (contentType.includes("application/json")) {
			const json: unknown = await response.json().catch(() => null);
			const legacy = z
				.object({
					ok: z.literal(true),
					text: z.string(),
					modelId: z.string().optional(),
				})
				.safeParse(json);
			if (legacy.success) {
				args.onDelta({
					reasoning: "",
					text: legacy.data.text,
					handoffs: [],
					connects: [],
					siteClones: [],
					siteDesigns: [],
					companyResearch: [],
				});
				return {
					ok: true,
					text: legacy.data.text,
					modelId: legacy.data.modelId ?? modelIdHeader,
					handoffs: [],
					connects: [],
					siteClones: [],
					siteDesigns: [],
					companyResearch: [],
				};
			}
			const err = chatErrSchema.safeParse(json);
			return {
				ok: false,
				error: err.success ? err.data.error : "invalid_response",
			};
		}

		if (!response.body) {
			return { ok: false, error: "empty_stream" };
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";
		let reasoning = "";
		let text = "";
		let handoffs: ChatHandoff[] = [];
		let connects: ChatConnect[] = [];
		let siteClones: ChatSiteClone[] = [];
		let siteDesigns: ChatSiteDesign[] = [];
		let companyResearch: ChatCompanyResearch[] = [];
		let modelId = modelIdHeader;
		let streamError: string | null = null;

		const handleLine = (line: string) => {
			const trimmed = line.trim();
			if (!trimmed) return;
			let parsed: unknown;
			try {
				parsed = JSON.parse(trimmed) as unknown;
			} catch {
				text = trimmed;
				args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
				return;
			}
			const event = streamEventSchema.safeParse(parsed);
			if (!event.success) return;
			switch (event.data.type) {
				case "reasoning":
					reasoning = event.data.text;
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				case "text":
					text = event.data.text;
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				case "handoff": {
					const snap = handoffSnapshotSchema.safeParse({
						handoffId: event.data.handoffId,
						agentType: event.data.agentType,
						agentLabel: event.data.agentLabel,
						task: event.data.task,
						phase: event.data.phase,
						note: event.data.note,
						screenshotUrl: event.data.screenshotUrl,
						status: event.data.status,
						summary: event.data.summary,
						approvalId: event.data.approvalId,
						openAgentId: event.data.openAgentId,
						previewUrl: event.data.previewUrl,
					});
					if (snap.success) {
						handoffs = upsertHandoff(handoffs, snap.data);
						args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					}
					return;
				}
				case "connect": {
					const snap = connectSnapshotSchema.safeParse({
						connectId: event.data.connectId,
						provider: event.data.provider,
						label: event.data.label,
						phase: event.data.phase,
						reason: event.data.reason,
						authorizeUrl: event.data.authorizeUrl,
						portfolioCount: event.data.portfolioCount,
					});
					if (snap.success) {
						connects = upsertConnect(connects, snap.data);
						args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					}
					return;
				}
				case "site_design": {
					siteDesigns = upsertSiteDesign(siteDesigns, event.data.siteId, {
						name: event.data.name,
						sourceUrl: event.data.sourceUrl,
						phase: event.data.phase,
						status: event.data.status,
						progress: event.data.progress,
						previewUrl: event.data.previewUrl,
						lastError: event.data.lastError,
						keep: event.data.keep,
						add: event.data.add,
						drop: event.data.drop,
						stills: event.data.stills,
						lastFile: event.data.lastFile,
						filesWritten: event.data.filesWritten,
					});
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				}
				case "site_publish": {
					const publishPhase =
						event.data.phase === "pending_approval"
							? "pending_approval"
							: event.data.phase === "deployed"
								? "deployed"
								: event.data.phase === "failed"
									? "failed"
									: "publishing";
					siteClones = upsertSiteClone(siteClones, event.data.siteId, {
						publishPhase,
						publishApprovalId: event.data.approvalId ?? null,
						deployStatus: event.data.deployStatus ?? null,
						repoUrl: event.data.repoUrl ?? undefined,
						publishedUrl: event.data.publishedUrl ?? undefined,
						hostname: event.data.hostname ?? undefined,
						takesPayments: event.data.takesPayments ?? undefined,
						...(event.data.siteStatus === "published" ? { status: "published" } : {}),
					});
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				}
				case "company_research": {
					companyResearch = upsertCompanyResearch(companyResearch, {
						packId: event.data.packId,
						siteUrl: event.data.siteUrl,
						phase: event.data.phase,
						status: event.data.status,
						progress: event.data.progress,
						brandName: event.data.brandName,
						lastError: event.data.lastError,
						card: event.data.card ?? null,
					});
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				}
				case "done":
					if (event.data.text !== undefined) text = event.data.text;
					if (event.data.reasoning !== undefined) {
						reasoning = event.data.reasoning;
					}
					if (event.data.modelId) modelId = event.data.modelId;
					args.onDelta({ reasoning, text, handoffs, connects, siteClones, siteDesigns, companyResearch });
					return;
				case "error":
					streamError = event.data.error;
					return;
				default: {
					const _exhaustive: never = event.data;
					return _exhaustive;
				}
			}
		};

		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = lines.pop() ?? "";
			for (const line of lines) handleLine(line);
		}
		buffer += decoder.decode();
		if (buffer.trim()) handleLine(buffer);

		if (streamError) {
			return { ok: false, error: streamError };
		}

		const finalText = text.trimEnd();
		if (
			!finalText &&
			!reasoning.trim() &&
			handoffs.length === 0 &&
			connects.length === 0 &&
			siteClones.length === 0 &&
			siteDesigns.length === 0 &&
			companyResearch.length === 0
		) {
			return { ok: false, error: "empty_stream" };
		}
		return {
			ok: true,
			text: finalText,
			reasoning: reasoning.trim() || undefined,
			modelId,
			handoffs,
			connects,
			siteClones,
			siteDesigns,
			companyResearch,
		};
	} catch (cause) {
		if (cause instanceof DOMException && cause.name === "AbortError") {
			return { ok: false, error: "aborted" };
		}
		return { ok: false, error: "network_error" };
	}
}

/** @deprecated Prefer `streamAgentChat`. */
export async function sendAgentChat(args: {
	agentId: string;
	messages: AgentChatTurn[];
}): Promise<StreamAgentChatResult> {
	return streamAgentChat({
		...args,
		onDelta: () => undefined,
	});
}

const deskHistorySchema = z.object({
	ok: z.literal(true),
	agentId: z.string(),
	recent: z.array(
		z.object({
			role: z.enum(["user", "assistant"]),
			content: z.string(),
			at: z.string(),
		}),
	),
});

/**
 * Restore DeskChatAgent transcript (Durable Object) for reconnect when
 * localStorage is empty or stale.
 */
export async function fetchDeskChatHistory(args: {
	agentId: string;
	threadId: string;
}): Promise<
	| { ok: true; recent: Array<{ role: "user" | "assistant"; content: string; at: string }> }
	| { ok: false; error: string }
> {
	try {
		const params = new URLSearchParams({
			agentId: args.agentId,
			threadId: args.threadId,
		});
		const response = await fetch(`/api/llm/history?${params}`, {
			credentials: "include",
		});
		if (!response.ok) {
			return { ok: false, error: `http_${response.status}` };
		}
		const json: unknown = await response.json().catch(() => null);
		const parsed = deskHistorySchema.safeParse(json);
		if (!parsed.success) return { ok: false, error: "bad_response" };
		return { ok: true, recent: parsed.data.recent };
	} catch {
		return { ok: false, error: "network_error" };
	}
}

/** Confirm assistant turn on DeskChatAgent after a successful stream (idempotent). */
export async function persistDeskChatTurn(args: {
	agentId: string;
	threadId: string;
	content: string;
}): Promise<{ ok: true } | { ok: false; error: string }> {
	const content = args.content.trim();
	if (!content) return { ok: false, error: "empty" };
	try {
		const response = await fetch("/api/llm/persist-turn", {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				agentId: args.agentId,
				threadId: args.threadId,
				role: "assistant",
				content,
			}),
		});
		if (!response.ok) return { ok: false, error: `http_${response.status}` };
		return { ok: true };
	} catch {
		return { ok: false, error: "network_error" };
	}
}
